/** The scope ledger's table: ONE header per WBS leaf (the hierarchy above it
 *  is a muted breadcrumb on the header, in WBS order), leaf groups with
 *  original/added/deleted counts, and one column row per activity (current
 *  values; staged changes show the original struck through). Deleted rows are
 *  red with a Restore. Headers are sticky BOTH
 *  ways (above stack at the top, below pin at the bottom) and clicking one
 *  scrolls its group to the top; the chevron collapses it. */
import React from "react";
import { statusBadgeClass, statusLabel } from "../../../../utils/format";
import { buildLeafList } from "../listGrouping";
import LevelPill from "../../WbsBoardView/LevelPill/LevelPill";
import type { LaneMember, LaneRowFacts } from "../ScopeLedger";
import { changeTagFor, hasRowChange } from "./changeTag";
import "./LedgerTable.css";

/** Sticky pitch — must match .ledger-leaf-header height + the colhead. */
const HDR = 28;
const COLHEAD = 22;

function scrollParentOf(el: HTMLElement): HTMLElement | null {
  let p = el.parentElement;
  while (p) {
    const st = getComputedStyle(p);
    if (/(auto|scroll)/.test(st.overflowY)) return p;
    p = p.parentElement;
  }
  return null;
}

interface Props {
  members: LaneMember[];
  rowFacts?: Map<number, LaneRowFacts>;
  anchorDir?: Map<number, "in" | "out" | "both">;
  showYear?: boolean;
  collapsedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  onRestore?: (activityId: number) => void;
  /** WBS-leaf level support (suppressed for plain / no-Takt plans). */
  readOnly?: boolean;
  /** Effective Takt level per leaf key (override ?? derived). null ⇒ unset. */
  levelByLeaf?: Map<string, string | null>;
  /** Mis-coded member count per leaf (drives the ⚠ pill + reconcile banner). */
  miscodeCountByLeaf?: Map<string, number>;
  /** Leaves flagged "not Takt work" — pill replaced by a ⚙ chip, no reconcile. */
  notaktLeaves?: Set<string>;
  /** Section Takt_C menu for the level chooser. */
  taktCMenu?: string[];
  onSetLeafLevel?: (leafKey: string, level: string) => void;
  /** Set every mis-coded member in this leaf to the effective level. */
  onReconcileLeaf?: (leafKey: string) => void;
  onToggleNotakt?: (leafKey: string) => void;
  /** Publish view: hide unchanged rows + show a per-row change-tag chip. A leaf
   *  with a pending reconcile banner (staged level override) stays visible even
   *  with zero changed rows — the override is itself a change. */
  changesOnly?: boolean;
}

const fmtD = (iso: string | null, year: boolean) => {
  if (!iso) return "—";
  const d = new Date(iso.slice(0, 10) + "T00:00:00");
  const md = `${d.getMonth() + 1}/${d.getDate()}`;
  return year ? `${md}/${String(d.getFullYear()).slice(2)}` : md;
};

/** A column cell: a staged change shows the ORIGINAL struck through first,
 *  then the current value highlighted. */
const Cell: React.FC<{
  cls: string;
  value: React.ReactNode;
  was?: string | number;
  title?: string;
}> = ({ cls, value, was, title }) => (
  <span
    className={cls + (was != null ? " ledger-changed" : "")}
    title={was != null ? `was: ${was}` : title}
  >
    {was != null && <s className="ledger-was">{was}</s>}
    {value}
  </span>
);

const LedgerTable: React.FC<Props> = ({
  members,
  rowFacts,
  anchorDir,
  showYear,
  collapsedGroups,
  onToggleGroup,
  onRestore,
  readOnly,
  levelByLeaf,
  miscodeCountByLeaf,
  notaktLeaves,
  taktCMenu,
  onSetLeafLevel,
  onReconcileLeaf,
  onToggleNotakt,
  changesOnly,
}) => {
  const groups = React.useMemo(() => buildLeafList(members), [members]);
  const levelSupported = Boolean(onSetLeafLevel);
  // Extra scroll room minted on demand so clicking ANY leaf header can bring
  // its group all the way to the top, even when the content below is short.
  const [tailPx, setTailPx] = React.useState(0);

  return (
    <div className={"ledger" + (changesOnly ? " ledger--changes-only" : "")}>
      <div className="ledger-colhead">
        <span className="ledger-code">Code</span>
        <span className="ledger-name">Activity</span>
        <span className="ledger-col-wc">Work code</span>
        <span className="ledger-col-dur">Dur</span>
        <span className="ledger-col-date">Start</span>
        <span className="ledger-col-date">Finish</span>
        <span className="ledger-col-status">Status</span>
        <span className="ledger-col-anchor">⚓</span>
        {changesOnly ? <span className="ledger-col-change">Change</span> : <span />}
      </div>
      {groups.map((g, gi) => {
        const collapsed = collapsedGroups.has(g.key);
        let orig = 0;
        let added = 0;
        let deleted = 0;
        let changed = 0;
        for (const lm of g.members) {
          const f = rowFacts?.get(lm.member.activity_id);
          if (f?.isNew) added += 1;
          else {
            orig += 1;
            if (f?.deleted) deleted += 1;
          }
          if (hasRowChange(f)) changed += 1;
        }
        // A staged level override surfaces as the reconcile banner; treat it as
        // a change so Changes-only never blanks a leaf that needs reconciling.
        const isNotaktLeaf = notaktLeaves?.has(g.key) ?? false;
        const leafEffLevel = levelByLeaf?.get(g.key) ?? null;
        const leafMiscode = miscodeCountByLeaf?.get(g.key) ?? 0;
        const leafHasReconcile =
          levelSupported && !isNotaktLeaf && leafMiscode > 0 && !!leafEffLevel;
        // Changes-only: a leaf with nothing changed (and no reconcile banner)
        // keeps its header but shows a single "no changes" line instead of rows.
        const leafNoChanges = !!changesOnly && changed === 0 && !leafHasReconcile;
        const header = (
          <div
            key={`h-${g.key}`}
            className="ledger-leaf-header"
            style={{
              top: COLHEAD + gi * HDR,
              bottom: (groups.length - 1 - gi) * HDR,
            }}
            onClick={(e) => {
              // Measure from the SENTINEL before the header — a stuck sticky
              // header reports its stuck position, not its place in the flow.
              const h = e.currentTarget as HTMLElement;
              const sentinel = h.previousElementSibling as HTMLElement | null;
              const scroller = scrollParentOf(h);
              if (!scroller || !sentinel) return;
              const top =
                scroller.scrollTop +
                sentinel.getBoundingClientRect().top -
                scroller.getBoundingClientRect().top -
                (COLHEAD + gi * HDR);
              const maxTop = scroller.scrollHeight - scroller.clientHeight;
              if (top > maxTop) setTailPx((t) => t + (top - maxTop));
              window.setTimeout(
                () => scroller.scrollTo({ top, behavior: "smooth" }),
                0,
              );
            }}
          >
            <button
              type="button"
              className="ledger-chevron"
              title={collapsed ? "Expand" : "Collapse"}
              onClick={(e) => {
                e.stopPropagation();
                onToggleGroup(g.key);
              }}
            >
              {collapsed ? "▸" : "▾"}
            </button>
            <strong className="ledger-leaf-name">{g.label}</strong>
            {levelSupported &&
              (notaktLeaves?.has(g.key) ? (
                <button
                  type="button"
                  className="ledger-notakt-chip"
                  disabled={readOnly}
                  title="Not Takt work — sequence only. Click to make it a Takt zone."
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!readOnly) onToggleNotakt?.(g.key);
                  }}
                >
                  ⚙ not Takt
                </button>
              ) : (
                <span onClick={(e) => e.stopPropagation()}>
                  <LevelPill
                    level={levelByLeaf?.get(g.key) ?? null}
                    miscodeCount={miscodeCountByLeaf?.get(g.key) ?? 0}
                    menu={taktCMenu ?? []}
                    readOnly={readOnly}
                    onSet={(lvl) => onSetLeafLevel!(g.key, lvl)}
                  />
                </span>
              ))}
            {g.breadcrumb.length > 0 && (
              <span className="ledger-leaf-crumbs" title={g.breadcrumb.join(" › ")}>
                {g.breadcrumb.join(" › ")}
              </span>
            )}
            <span className="ledger-leafcounts">
              {changesOnly ? (
                changed > 0 ? (
                  <span className="ledger-n ledger-n--changed" title="Changed in this draft">
                    {changed} change{changed === 1 ? "" : "s"}
                  </span>
                ) : leafHasReconcile ? (
                  <span className="ledger-n ledger-n--changed" title="Needs reconcile">
                    needs reconcile
                  </span>
                ) : (
                  <span className="ledger-n ledger-n--none" title="No changes">
                    no changes
                  </span>
                )
              ) : (
                <>
                  <span className="ledger-n ledger-n--orig" title="Original activities">
                    {orig}
                  </span>
                  {added > 0 && (
                    <span className="ledger-n ledger-n--added" title="Added in this draft">
                      +{added}
                    </span>
                  )}
                  {deleted > 0 && (
                    <span className="ledger-n ledger-n--deleted" title="Deleted in this draft">
                      −{deleted}
                    </span>
                  )}
                </>
              )}
            </span>
          </div>
        );
        // Zero-height layout anchor — the header's scroll-to-top target
        // (sticky elements can't measure their own flow position once stuck).
        const sentinel = (
          <div key={`s-${g.key}`} className="ledger-leaf-sentinel" aria-hidden="true" />
        );
        if (collapsed)
          return (
            <React.Fragment key={`g-${g.key}`}>
              {sentinel}
              {header}
            </React.Fragment>
          );
        const effLevel = leafEffLevel;
        const showReconcile = leafHasReconcile;
        const isMiscoded = (m: LaneMember["member"]): boolean => {
          if (!showReconcile || m.status === "TK_Complete") return false;
          const c = (m.takt_c || "").trim();
          return !!c && c.toUpperCase() !== effLevel!.toUpperCase();
        };
        return (
          <React.Fragment key={`g-${g.key}`}>
            {sentinel}
            {header}
            {showReconcile && (
              <div className="ledger-reconcile">
                <span>
                  ⚠ {leafMiscode} activit{leafMiscode === 1 ? "y" : "ies"} coded a different
                  level but filed as <b>{effLevel}</b>.
                </span>
                {onReconcileLeaf && !readOnly && (
                  <button type="button" onClick={() => onReconcileLeaf(g.key)}>
                    Set all to {effLevel}
                  </button>
                )}
              </div>
            )}
            {leafNoChanges && (
              <div className="ledger-nochange-line">— no changes in this area —</div>
            )}
            {g.members.map((lm) => {
              const m = lm.member;
        const f = rowFacts?.get(m.activity_id);
        if (changesOnly && !hasRowChange(f)) return null;
        const isDeleted = f?.deleted === true;
        // Completed work is locked history: show its actual dates, never a
        // replanned span or a "changed" flag (its restart/reend are NULL).
        const isDone = m.status === "TK_Complete";
        const anchor = anchorDir?.get(m.activity_id);
        return (
          <div
            key={m.activity_id}
            className={
              "ledger-row" +
              (lm.state === "completed" && !isDeleted ? " ledger-row--done" : "") +
              (f?.isNew && !isDeleted ? " ledger-row--new" : "") +
              (isDeleted ? " ledger-row--deleted" : "")
            }
            title={m.task_name}
          >
            <span className="ledger-code">{m.task_code}</span>
            <span className="ledger-name-wrap">
              <Cell cls="ledger-name" value={m.task_name} was={f?.was.name} title={m.task_name} />
              {isMiscoded(m) && (
                <span
                  className="ledger-miscode-chip"
                  title={`Coded ${m.takt_c} but this leaf is filed as ${effLevel}`}
                >
                  coded {m.takt_c} ⚠
                </span>
              )}
            </span>
            <span
              className={"ledger-col-wc" + (f?.was.code != null ? " ledger-changed" : "")}
              title={f?.was.code != null ? `was: ${f.was.code}` : undefined}
            >
              {f?.code ? (
                <span
                  className="ledger-takt-pill"
                  style={{ background: f.codeColor || "#4a4f57" }}
                >
                  {f.code}
                </span>
              ) : (
                <span className="ledger-dim">—</span>
              )}
            </span>
            <Cell
              cls="ledger-col-dur"
              value={Number.isFinite(f?.days) ? `${f!.days}d` : "—"}
              was={
                f?.was.days != null && Number.isFinite(f.was.days)
                  ? `${f.was.days}d`
                  : undefined
              }
            />
            <Cell
              cls="ledger-col-date"
              value={fmtD(isDone ? m.actual_start : (f?.start ?? m.restart_date), !!showYear)}
              was={!isDone && f?.was.start != null ? fmtD(f.was.start, !!showYear) : undefined}
            />
            <Cell
              cls="ledger-col-date"
              value={fmtD(isDone ? m.actual_finish : (f?.finish ?? m.reend_date), !!showYear)}
              was={!isDone && f?.was.finish != null ? fmtD(f.was.finish, !!showYear) : undefined}
            />
            <span className="ledger-col-status">
              <span className={statusBadgeClass(m.status)}>{statusLabel(m.status)}</span>
            </span>
            <span className="ledger-col-anchor">
              {anchor && (
                <span className={`ledger-anchor-tag ledger-anchor-tag--${anchor}`}>
                  {anchor === "in" ? "⚓ IN" : anchor === "out" ? "⚓ OUT" : "⚓ I/O"}
                </span>
              )}
            </span>
            <span className="ledger-col-restore">
              {changesOnly ? (
                (() => {
                  const tag = changeTagFor(f);
                  return tag ? (
                    <span className={`ledger-change-tag ledger-change-tag--${tag.kind}`}>
                      {tag.label}
                    </span>
                  ) : null;
                })()
              ) : isDeleted && onRestore ? (
                <button
                  type="button"
                  className="ledger-restore"
                  title="Undo the delete — restores this activity to the board on its own row"
                  onClick={() => onRestore(m.activity_id)}
                >
                  ↺ Restore
                </button>
              ) : null}
            </span>
          </div>
        );
            })}
          </React.Fragment>
        );
      })}
      {tailPx > 0 && <div style={{ height: tailPx }} aria-hidden="true" />}
    </div>
  );
};

export default LedgerTable;
